// <DrillSheet> — canonical ClientHub kit component (Epic 1 · S1.3).
//
// The single in-page drill destination (00_CONTRACT.md §12). Every clickable
// number opens this, from the same edge, with one learned gesture. Stacks and
// reverses (Set → item → related entity) with a back affordance; Esc unwinds.
// Never navigates away — p_scope carries the page's live filters so a drill
// never loses them.
//
// Built on shared/DrillOver (the existing stacked slide-over with focus-trap,
// Esc, back affordance). This component adds the data layer: it calls
// drill(what, id, scope) from api/hub.js, manages the frame stack, and maps
// the fn_drill response to DrillOver's row shape.
//
// p_what values: bucket · task_commitments · task_messages · status_history ·
// task_attachments · task_subtasks · throughput_week · client_waiting ·
// position · past_p90 · person
//
// RPC shape is UNCONFIRMED (see api/hub.js). The component maps defensively.

import React, { useCallback, useEffect, useState } from "react";
import DrillOver from "../../shared/DrillOver";
import { drill as drillRpc } from "../../../api/hub";
import { ErrorState } from "../../shared/StateMessage";
import { SkeletonText } from "../../shared/Skeleton";

// Map a fn_drill response frame to DrillOver's expected shape.
// fn_drill → { kind, label, rows[] }. Each row may have { primary, secondary,
// metric, drill } or different field names — map defensively against the
// UNCONFIRMED contract.
const mapFrame = (frame) => {
  if (!frame) return { kind: "", label: "", rows: [] };
  const rows = Array.isArray(frame.rows) ? frame.rows : [];
  return {
    kind: frame.kind || "",
    label: frame.label || frame.kind || "",
    rows: rows.map((r) => ({
      primary: r.primary || r.label || r.name || r.title || String(r.id ?? ""),
      secondary: r.secondary || r.subtitle || r.description || r.says || null,
      metric: r.metric != null ? r.metric : r.count ?? r.value ?? null,
      drill: r.drill || null,
      ...r, // preserve raw fields for the onRowDrill handler
    })),
  };
};

/**
 * @param {object} props
 * @param {boolean} props.open       Controlled open/close.
 * @param {() => void} props.onClose Close all frames.
 * @param {string} props.what        Initial p_what (bucket, task_commitments, …).
 * @param {string} props.id          Initial p_id.
 * @param {object} [props.scope]     p_scope — live filters from the page.
 * @param {number} [props.limit=50]  p_limit.
 * @param {Function} [props.renderBody] Override the default row renderer.
 */
export default function DrillSheet({
  open,
  onClose,
  what,
  id,
  scope = "client",
  limit = 50,
  renderBody,
}) {
  const [stack, setStack] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch a frame and push it onto the stack.
  const fetchFrame = useCallback(
    async (pWhat, pId, pScope) => {
      setLoading(true);
      setError(null);
      try {
        const res = await drillRpc(pWhat, pId, pScope);
        const frame = mapFrame(res?.data);
        setStack((prev) => [...prev, frame]);
      } catch (err) {
        setError(err?.message || "Couldn't load this drill.");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // When opened, fetch the initial frame.
  useEffect(() => {
    if (open && what && id) {
      setStack([]);
      fetchFrame(what, id, scope);
    }
    // Clear on close.
    if (!open) {
      setStack([]);
      setError(null);
    }
  }, [open, what, id, scope, fetchFrame]);

  // A row with `drill` truthy → fetch the next frame and push.
  const handleRowDrill = useCallback(
    (row) => {
      const d = row?.drill;
      if (!d) return;
      // `drill` may be: { what, id } | string (the what, use row.id) | true
      const nextWhat = typeof d === "object" ? d.what : typeof d === "string" ? d : row.kind || what;
      const nextId = typeof d === "object" ? d.id : row.id || id;
      if (nextWhat && nextId) fetchFrame(nextWhat, nextId, scope);
    },
    [fetchFrame, scope, what, id],
  );

  // Pop one frame (back button).
  const handleBack = useCallback(() => {
    setStack((prev) => prev.slice(0, -1));
  }, []);

  if (!open) return null;

  // Error overlay — still renders the sheet so the user can back out.
  if (error && stack.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex">
        <div className="flex-1 bg-black/20 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
        <div className="w-full md:w-[600px] md:max-w-[calc(100vw-60px)] h-full bg-white dark:bg-gray-900 shadow-2xl border-l dark:border-gray-800 flex flex-col">
          <div className="flex items-center justify-between px-5 py-4 border-b dark:border-gray-800">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Detail</h2>
            <button type="button" onClick={onClose} className="text-sm text-gray-500 hover:text-gray-900" aria-label="Close">Close</button>
          </div>
          <div className="flex-1 p-5">
            <ErrorState variant="inset" message={error} onRetry={() => fetchFrame(what, id, scope)} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <DrillOver
      stack={stack}
      onBack={handleBack}
      onClose={onClose}
      onRowDrill={handleRowDrill}
      renderBody={renderBody}
      loading={loading}
    />
  );
}
